import { useEffect, useRef, useState, type FormEvent } from 'react'

import { TURNSTILE_SITE_KEY } from '../../../config'

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      reset: (id: string) => void
    }
  }
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

type Status = 'idle' | 'sending' | 'sent' | 'error'

/**
 * Turnstile is loaded on demand and rendered explicitly: the implicit
 * scan-on-load mode races React's first render. The widget hands back a
 * one-shot token, which /api/demo-request verifies server-side.
 */
function useTurnstile(onToken: (token: string) => void) {
  const ref = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string>()

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !ref.current) return
    const el = ref.current

    const render = () => {
      if (!window.turnstile || widgetId.current) return
      widgetId.current = window.turnstile.render(el, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'dark',
        appearance: 'interaction-only',
        callback: onToken,
        'expired-callback': () => onToken(''),
      })
    }

    let script = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = TURNSTILE_SRC
      script.async = true
      document.head.appendChild(script)
    }
    if (window.turnstile) render()
    else script.addEventListener('load', render)
    return () => script?.removeEventListener('load', render)
  }, [])

  const reset = () => {
    if (widgetId.current) window.turnstile?.reset(widgetId.current)
    onToken('')
  }

  return { ref, reset }
}

export default function Demo() {
  const [status, setStatus] = useState<Status>('idle')
  const [token, setToken] = useState('')
  const turnstile = useTurnstile(setToken)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const data = Object.fromEntries(new FormData(e.currentTarget))
    setStatus('sending')
    try {
      const res = await fetch('/api/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, turnstileToken: token }),
      })
      if (!res.ok) throw new Error(String(res.status))
      setStatus('sent')
    } catch {
      setStatus('error')
      // Tokens are single-use; get a fresh one for the retry.
      turnstile.reset()
    }
  }

  return (
    <section id="demo" className="lp-demo">
      <h2>You deliver the warfighter&rsquo;s edge. We deliver yours.</h2>
      <p>
        Test Overmatch with a requirement on your desk today. Thirty minutes,
        working session, no slide deck.
      </p>

      {status === 'sent' ? (
        <p className="lp-demo__done" role="status">
          Thank you for your interest, we will be in touch soon.
        </p>
      ) : (
        <form className="lp-demo__form" onSubmit={submit}>
          <div className="lp-demo__fields">
            <label>
              <span>First name</span>
              <input name="firstName" autoComplete="given-name" required maxLength={100} />
            </label>
            <label>
              <span>Last name</span>
              <input name="lastName" autoComplete="family-name" required maxLength={100} />
            </label>
            <label className="lp-demo__wide">
              <span>Work email</span>
              <input name="email" type="email" autoComplete="email" required maxLength={254} />
            </label>
            {/* Honeypot — hidden from people, irresistible to form bots. */}
            <label className="lp-demo__hp" aria-hidden="true">
              <span>Website</span>
              <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <div ref={turnstile.ref} className="lp-demo__challenge" />

          <button
            type="submit"
            className="lp-btn lp-btn--amber"
            disabled={status === 'sending'}
          >
            {status === 'sending' ? 'Sending…' : 'Request a demo'}
          </button>

          {status === 'error' && (
            <p className="lp-demo__error" role="alert">
              That didn&rsquo;t go through. Please try again in a moment.
            </p>
          )}
        </form>
      )}
    </section>
  )
}
